import { Node, mergeAttributes } from '@tiptap/core';

/**
 * CourseCards — a row of visual cards inside a lesson.
 *
 * Robert, 8 Sep: "add visual cards to tiptap blocks so we can add more
 * visualization elements to our courses." The immediate need is the last step
 * of orientation, where six text links asked somebody to choose the thing the
 * whole course exists to produce; a wall of underlined text is a poor way to
 * put a choice in front of a person.
 *
 * A card carries an image, a title, a line of copy and one action. The action
 * is a plain link, which is what lets it reuse behaviour the course viewer
 * already has:
 *
 *   href "#choose:<courseId>"  finishes THIS course, awards its badge and
 *                              starts that one — one click, no coming back
 *   any other href             an ordinary link
 *
 * Attributes are one JSON string rather than parallel arrays, so a card cannot
 * end up with somebody else's image when an author reorders them.
 *
 *   cards: '[{"title":"...","blurb":"...","image":"...","href":"...","meta":"5 sessions"}]'
 */
export const CourseCards = Node.create({
  name: 'courseCards',

  group: 'block',

  atom: true,

  addAttributes() {
    return {
      cards: {
        default: '[]',
        parseHTML: (element) => element.getAttribute('data-cards') || '[]',
        renderHTML: (attributes) => ({ 'data-cards': attributes.cards }),
      },
      // 'grid' (default) or 'list' for narrow contexts.
      layout: {
        default: 'grid',
        parseHTML: (element) => element.getAttribute('data-layout') || 'grid',
        renderHTML: (attributes) => ({ 'data-layout': attributes.layout }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="course-cards"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'course-cards',
        class: 'course-cards',
        contenteditable: 'false',
      }),
    ];
  },

  addNodeView() {
    return ({ node }) => {
      const attrs = node.attrs || {};
      let cards = [];
      try {
        cards = JSON.parse(attrs.cards || '[]');
      } catch (e) {
        cards = [];
      }

      const esc = (s) =>
        String(s == null ? '' : s).replace(
          /[&<>"']/g,
          (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]
        );

      const dom = document.createElement('div');
      dom.className = 'course-cards';
      dom.setAttribute('data-type', 'course-cards');
      dom.setAttribute('data-cards', attrs.cards || '[]');
      dom.setAttribute('data-layout', attrs.layout || 'grid');
      dom.contentEditable = 'false';
      // One column by default; the stylesheet widens it above 700px to the
      // number of columns asked for here. A set of three stays three rather
      // than collapsing to two inside the lesson's reading width.
      // Four cards under a three-column rule leaves the fourth alone on its own
      // row with two empty columns beside it, which reads as a mistake. Four go
      // two-by-two instead. Five or more still take three across, where the
      // short last row is obviously a wrap rather than an orphan.
      const n = cards.length || 1;
      const wanted = attrs.layout === 'list' ? 1 : n === 4 ? 2 : Math.min(n, 3);
      dom.style.setProperty('--cards', String(wanted));
      dom.style.cssText += 'display:grid;gap:1rem;margin:1.25rem 0;grid-template-columns:1fr;';

      cards.forEach((card) => {
        // No destination means this card explains something rather than going
        // somewhere. A link that leads nowhere is worse than not being a link.
        const clickable = !!card.href;
        const a = document.createElement(clickable ? 'a' : 'div');
        if (clickable) a.href = card.href;
        // Anything that leaves this site opens a new tab (Robert, 8 Sep: "The
        // one page version needs to open a new tab or modal"). A learner who
        // clicks a reference mid-lesson should still have the lesson behind
        // them, with their place and their unsaved answers intact. In-app hrefs
        // keep navigating normally; newTab forces it for anything else.
        const leavesTheSite =
          /^https?:\/\//i.test(card.href || '') &&
          typeof location !== 'undefined' &&
          card.href.indexOf(location.origin) !== 0;
        if (clickable && (leavesTheSite || card.newTab)) {
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
        }
        a.className = 'course-card';
        a.style.cssText =
          'display:flex;flex-direction:column;border:1px solid #e5e7eb;border-radius:12px;' +
          'overflow:hidden;background:#fff;text-decoration:none;color:inherit;' +
          'box-shadow:0 1px 3px rgba(0,0,0,0.06);transition:box-shadow .15s,transform .15s;';
        if (clickable)
          a.addEventListener('mouseenter', () => {
            a.style.boxShadow = '0 6px 18px rgba(43,107,95,0.18)';
            a.style.transform = 'translateY(-2px)';
          });
        if (clickable)
          a.addEventListener('mouseleave', () => {
            a.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
            a.style.transform = 'none';
          });

        if (card.iconImage) {
          // A real generated image rather than a traced outline. Robert, 8 Sep:
          // "svg just doesn't create solid icons or images" -- running a good
          // image through potrace keeps the silhouette and throws away
          // everything that made it worth looking at.
          const cap = document.createElement('div');
          cap.style.cssText =
            'padding:1.1rem 1rem 0;display:flex;align-items:center;justify-content:center;';
          const im = document.createElement('img');
          im.src = card.iconImage;
          im.alt = '';
          im.loading = 'lazy';
          im.style.cssText =
            'width:96px;height:96px;object-fit:contain;border-radius:16px;display:block;background:#F7F5F0;';
          im.addEventListener('error', () => cap.remove());
          cap.appendChild(im);
          a.appendChild(cap);
        } else if (card.icon) {
          // Sprite icons come from platform-chrome, which every page loads.
          const cap = document.createElement('div');
          cap.style.cssText =
            'padding:1.1rem 1rem 0;display:flex;align-items:center;justify-content:center;';
          cap.innerHTML =
            '<span style="display:flex;align-items:center;justify-content:center;width:52px;height:52px;' +
            'border-radius:14px;background:#EAF3F0;">' +
            '<svg class="nav-ico" aria-hidden="true" focusable="false" style="width:26px;height:26px;color:#2B6B5F;">' +
            '<use href="#i-' +
            esc(card.icon) +
            '"></use></svg></span>';
          a.appendChild(cap);
        } else if (card.image) {
          const img = document.createElement('img');
          img.src = card.image;
          img.alt = '';
          img.loading = 'lazy';
          img.style.cssText =
            'width:100%;height:120px;object-fit:cover;display:block;background:#f3f4f6;';
          // A course with no hero should not leave a broken frame behind.
          img.addEventListener('error', () => img.remove());
          a.appendChild(img);
        }

        const body = document.createElement('div');
        // An icon card is a small poster and reads better centred; a course
        // card stays left aligned like the rest of the page.
        body.style.cssText =
          'padding:0.85rem 1rem 1rem;display:flex;flex-direction:column;gap:0.35rem;flex:1;' +
          (card.icon || card.iconImage ? 'text-align:center;align-items:center;' : '');
        body.innerHTML =
          (card.meta
            ? `<span style="font-size:0.68rem;font-weight:700;letter-spacing:0.06em;text-transform:uppercase;color:#2B6B5F;">${esc(card.meta)}</span>`
            : '') +
          `<span style="font-weight:700;font-size:0.98rem;line-height:1.3;color:#1f2937;">${esc(card.title || '')}` +
          // Say so before the click, not after it.
          (a.target === '_blank'
            ? '<span style="color:#2B6B5F;font-weight:700;"> \u2197</span>'
            : '') +
          '</span>' +
          (card.blurb
            ? `<span style="font-size:0.84rem;line-height:1.5;color:#4b5563;">${esc(card.blurb)}</span>`
            : '') +
          (clickable
            ? `<span style="margin-top:auto;padding-top:0.6rem;font-size:0.85rem;font-weight:700;color:#2B6B5F;">${esc(card.cta || 'Start this')} &rarr;</span>`
            : '');
        // Named so an action can drop a status line inside the card rather
        // than replacing it. #enroll: uses this to say "added to your
        // dashboard" without the card moving or losing its image.
        body.className = 'course-card-body';
        a.appendChild(body);
        dom.appendChild(a);
      });

      return { dom };
    };
  },

  addCommands() {
    return {
      setCourseCards:
        (attributes) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs: attributes }),
    };
  },
});

export default CourseCards;
